import React, {useMemo} from 'react';
import {useTypedSelector} from '@common/hooks/useTypedSelector';
import Step from '@components/PageStep';
import {EBlockPosition, EComponent} from '@redux/types';
import MainContacts from '@components/MainContacts';
import Picture from '@common/components/common/Picture';
import ProjectGrid from '@common/components/ProjectGrid';
import Tags from '@components/Tags';
import {useTranslation} from '@common/hooks/useTranslation';
import {ITranslation} from '@common/types';
import EmbedMedia from '@common/components/common/EmbedMedia';
import {useGetMainStepCount} from '@common/hooks/useGetMainStepCount';
import RichTextContent from '@common/components/RichTextContent';
import {isNotEmptyRichContent} from '@common/functions';
import Text from '@common/components/common/Text';


export const ProjectDetail = () => {
  const t = useTranslation('projects') as ITranslation['projects'];
  const {project} = useTypedSelector(state => state.projects);
  const mainStepCount = useGetMainStepCount(project?.steps || []);

  const similarProjectsStepNumber = useMemo(() =>
    !mainStepCount ? `01` : mainStepCount + 1 > 10 ? mainStepCount + 1 : `0${mainStepCount + 1}`,
    [mainStepCount]);
  const contactStepNumber = useMemo(() => mainStepCount + (!!project?.similarProjects?.length ? 2 : 1),
    [mainStepCount, project]);

  return (
    <>
      <section className="inner-title inner-title--margin-short">
        <div className="container">
          <h1 className="inner-title__text">
            <Text text={project?.detail?.title ?? project?.title}/>
          </h1>
        </div>
      </section>

      <section className="inner-grid inner-grid--no-padding inner-grid--aside-left inner-grid--no-margin">
        <div className="container">
          <div className="inner-grid__aside">
            <Tags classNames={'inner-styled-list inner-styled-list--aside'}
                  tags={project?.extendTags?.map(t => t.name) || []}/>
          </div>
          <div className="inner-grid__content">
            <h2 className="visually-hidden">{t.project.visuallyHidden}</h2>
            {!isNotEmptyRichContent(project?.detail?.text) ? (
              <div className="inner-simple-text"><p>{project?.detail?.description}</p></div>
            ) : (
              <div className="inner-simple-text inner-simple-text--mobile-margin">
                <RichTextContent content={project?.detail?.text}/>
              </div>
            )}
          </div>
          {!!project?.detail?.media?.image ? (
            <Picture
              media={project?.detail?.media || null}
              wrapperClassName={'inner-grid__content-max mt-65 mt-md-45 mt-sm-40'}
              wrapperCaptionClassName={'inner-grid__content-max'}
            />
          ) : (
            <>{project?.detail?.embed && (
              <div className={"inner-grid__content-max"}>
                <EmbedMedia embed={project.detail.embed}/>
              </div>
            )} </>
          )}
        </div>
      </section>

      {project?.steps?.map((item, index) => (
        <Step
          key={`step_${index}`}
          count={item.count}
          step={item.step}
          stepChildren={item.children}
        />
      ))}

      {!!project?.similarProjects?.length && (
        <section className="inner-grid">
          <div className="container">
            <div className="section-heading">
              <span className="section-heading__count">{similarProjectsStepNumber}</span>
              <h2 className="section-heading__title">
                <Text text={t.project?.similarProjects?.title}/>
              </h2>
            </div>
            <div className="project">
              <ProjectGrid projects={project?.similarProjects || []}/>
            </div>
          </div>
        </section>
      )}

      <Step
        count={contactStepNumber}
        childrenPosition={EBlockPosition.insideStep}
        step={{
          __component: EComponent.STEP,
          name: t.project?.steps?.cooperation?.title || '',
          displayStepNumber: true,
          childPosition: EBlockPosition.outsideStep
        }}
      >
        <MainContacts withPadding={false}/>
      </Step>
    </>
  )
}
